<script>
export default {
    name: 'ExperienceSlider',
    props: { value: { type: Number, required: true }, valueText: { type: String, required: true } },
    emits: ['change', 'commit', 'cancel', 'navigate'],
    data() { return { pointerId: null, grabOffset: 0, lastValue: 0 }; },
    computed: {
        thumbStyle() { return { left: `calc(24px + (100% - 48px) * ${this.value / 100})` }; },
    },
    methods: {
        valueAt(clientX) {
            const rect = this.$refs.control.getBoundingClientRect();
            const travel = Math.max(1, rect.width - 48);
            const x = clientX - rect.left - this.grabOffset;
            // Invert the 25px Standard offset, which fades out by the Deluxe checkpoint.
            const deluxeX = 24 + travel * .78;
            const ratio = x <= deluxeX ? (x + 1) / (travel + 25 / .78) : (x - 24) / travel;
            return Math.max(0, Math.min(100, ratio * 100));
        },
        start(event) {
            if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
            event.preventDefault();
            this.$refs.thumb.focus({ preventScroll: true });
            const fromThumb = this.$refs.thumb.contains(event.target);
            const rect = this.$refs.thumb.getBoundingClientRect();
            this.grabOffset = fromThumb ? event.clientX - (rect.left + rect.width / 2) : 0;
            this.pointerId = event.pointerId;
            this.$refs.control.setPointerCapture(event.pointerId);
            this.lastValue = fromThumb ? this.value : this.valueAt(event.clientX);
            this.$emit('change', this.lastValue);
        },
        move(event) {
            if (event.pointerId !== this.pointerId) return;
            this.lastValue = this.valueAt(event.clientX);
            this.$emit('change', this.lastValue);
        },
        finish(event) {
            if (event.pointerId !== this.pointerId) return;
            this.lastValue = this.valueAt(event.clientX);
            this.pointerId = null;
            this.$refs.control.releasePointerCapture(event.pointerId);
            this.$emit('commit', this.lastValue);
        },
        cancel() {
            if (this.pointerId === null) return;
            this.pointerId = null;
            this.$emit('cancel');
        },
        keydown(event) {
            if (!['ArrowRight','ArrowLeft','ArrowUp','ArrowDown','Home','End','PageUp','PageDown'].includes(event.key)) return;
            event.preventDefault();
            this.$emit('navigate', event.key);
        },
    },
};
</script>

<template>
    <div class="experience-slider vue-slider" data-slot="slider">
        <div ref="control" class="vue-slider-control" @pointerdown="start" @pointermove="move" @pointerup="finish" @pointercancel="cancel" @lostpointercapture="cancel">
            <div data-slot="slider-track"><div data-slot="slider-range" :style="{width: `${value}%`}" /></div>
            <div ref="thumb" role="slider" tabindex="0" aria-label="Therapist experience" aria-orientation="horizontal" :aria-valuemin="0" :aria-valuemax="100" :aria-valuenow="value" :aria-valuetext="valueText" data-slot="slider-thumb" :style="thumbStyle" @keydown="keydown" />
        </div>
    </div>
</template>

<style scoped>
.vue-slider {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    width: 100%;
    height: 32px;
    touch-action: none;
    user-select: none;
}

.vue-slider-control {
    position: relative;
    width: 100%;
    height: 32px;
    display: flex;
    align-items: center;
    touch-action: none;
}

.vue-slider [data-slot=slider-track] {
    position: relative;
    flex: 1;
    height: 100%;
    border-radius: 999px;
    background: transparent;
}

.vue-slider [data-slot=slider-range] {
    height: 100%;
    background: transparent;
}

.vue-slider [data-slot=slider-thumb] {
    position: absolute;
    top: 50%;
    translate: -50% -50%;
    transform: translateX(var(--standard-offset, 0px));
    width: 48px;
    height: 48px;
    border: 4px solid #b57927;
    border-radius: 50%;
    background: #ff6624;
    box-shadow: none;
    z-index: 4;
    cursor: grab;
    touch-action: none;
    transition: box-shadow .2s;
}

.vue-slider [data-slot=slider-thumb]::after {
    content: '';
    position: absolute;
    inset: -8px;
}

.vue-slider [data-slot=slider-thumb]:active { cursor: grabbing; }
.vue-slider [data-slot=slider-thumb]:focus-visible { outline: 2px solid #fff; outline-offset: 4px; }

/* States driven by the parent's .experience-bar attributes */
.experience-bar[data-powered=true] [data-slot=slider-thumb] {
    background: #ed992d;
    border-color: #fff;
    box-shadow: 0 0 0 3px #ffffff25;
}
.experience-bar[data-turbo=true] [data-slot=slider-thumb] { background: #7d53ff; }
.experience-bar[data-idle=true] [data-slot=slider-thumb] { animation: thumb-invitation 2s ease-in-out infinite; }

@keyframes thumb-invitation {
    0%, 22%, 100% { transform: translateX(var(--standard-offset, 0px)); }
    48% { transform: translateX(calc(var(--standard-offset, 0px) - 7px)); }
    67% { transform: translateX(calc(var(--standard-offset, 0px) + 2px)); }
    80% { transform: translateX(calc(var(--standard-offset, 0px) - 1px)); }
}

@media (prefers-reduced-motion: reduce) {
    .experience-bar[data-idle=true] [data-slot=slider-thumb] { animation: none; }
}
</style>